import { DateTime } from "luxon";
import { isEmpty, isNil } from "ramda";
import {
  formatThaiShortBuddhistDateRange,
  toDateTime,
} from "@/lib/dateTime";
import {
  convertSatangToBahtAndRoundTwoDecimal,
  parseCurrencyInputNumber,
} from "@/lib/formatter";

function getFirstNonNilValue(...values) {
  const foundValue = values.find((value) => !isNil(value));

  return isNil(foundValue) ? null : foundValue;
}

function isMainServiceOption(option) {
  return option && option.extra_option !== true;
}

export function getServiceProductCostForForm(product) {
  if (!product) return null;

  if (!isNil(product.cost_price)) {
    return convertSatangToBahtAndRoundTwoDecimal(product.cost_price);
  }

  return getFirstNonNilValue(product.cost, product.costPrice);
}

export function getServiceProductPromotionPriceForForm(product) {
  if (!product) return null;

  if (!isNil(product.promotionPrice)) {
    return product.promotionPrice;
  }

  if (isNil(product.promotion_price)) return null;

  if (!isNil(product.cost) && isNil(product.cost_price)) {
    return product.promotion_price;
  }

  return convertSatangToBahtAndRoundTwoDecimal(product.promotion_price);
}

function getServiceProductPromotionStartedAt(product) {
  if (!product) return null;

  return getFirstNonNilValue(
    product.promotion_started_at,
    product.promotionStartedAt
  );
}

function getServiceProductPromotionEndedAt(product) {
  if (!product) return null;

  return getFirstNonNilValue(product.promotion_ended_at, product.promotionEndedAt);
}

function getServiceProductPromotionActiveFlag(product) {
  if (!product) return null;

  return getFirstNonNilValue(product.is_promotion, product.isPromotion);
}

function toPromotionDateTime(date) {
  const dateTime = toDateTime(date);

  if (!dateTime) return null;

  return dateTime.isValid ? dateTime.setZone("utc+7") : null;
}

function hasPromotionPeriodEnded(endedAt, now = DateTime.local()) {
  const endedAtDateTime = toPromotionDateTime(endedAt);

  if (!endedAtDateTime) return false;

  return endedAtDateTime.endOf("day") < now.setZone("utc+7");
}

function getServiceProductPromotionData(product) {
  const promotionPrice = getServiceProductPromotionPriceForForm(product);
  const isPromotion = getServiceProductPromotionActiveFlag(product);
  const promotionEndedAt = getServiceProductPromotionEndedAt(product);
  const canUsePromotion =
    !isNil(promotionPrice) &&
    isPromotion !== false;
  const hasPromotionPeriodExpired =
    canUsePromotion &&
    hasPromotionPeriodEnded(promotionEndedAt);
  const hasPromotion =
    canUsePromotion &&
    !hasPromotionPeriodExpired;

  return {
    hasPromotion,
    hasPromotionPeriodMismatch: false,
    promotionPrice,
    originalCost: getServiceProductCostForForm(product),
    promotionStartedAt: getServiceProductPromotionStartedAt(product),
    promotionEndedAt,
  };
}

export function getServiceProductCostData(product) {
  const promotionData = getServiceProductPromotionData(product);
  const cost = promotionData.hasPromotion
    ? promotionData.promotionPrice
    : getServiceProductCostForForm(product);

  return {
    cost,
    hasPromotion: promotionData.hasPromotion,
    hasPromotionPeriodMismatch: promotionData.hasPromotionPeriodMismatch,
    promotionPrice: promotionData.promotionPrice,
    originalCost: promotionData.originalCost,
    promotionStartedAt: promotionData.promotionStartedAt,
    promotionEndedAt: promotionData.promotionEndedAt,
    promotionPeriod: promotionData.hasPromotion
      ? formatThaiShortBuddhistDateRange(
          promotionData.promotionStartedAt,
          promotionData.promotionEndedAt
        )
      : null,
  };
}

export function getServiceProductCostSummary(product) {
  const productCostData = getServiceProductCostData(product);

  if (!productCostData.hasPromotion) {
    return {
      cost: productCostData.cost,
      hasPromotion: false,
      originalCost: null,
      promotionPeriod: null,
    };
  }

  return {
    cost: productCostData.cost,
    hasPromotion: true,
    originalCost: productCostData.originalCost,
    promotionPeriod: productCostData.promotionPeriod,
  };
}

export function shouldDisableTotalCostInput({ disabled, hasQuota }) {
  return disabled || hasQuota;
}

function normalizeServiceProduct(product, variantId) {
  const selectedVariantId =
    arguments.length > 1
      ? variantId
      : getFirstNonNilValue(product.variantId, product.variant_id);
  const isPromotion = getServiceProductPromotionActiveFlag(product);
  const normalizedProduct = {
    id: getFirstNonNilValue(product.id, product.product_id, product.productId),
    name: product.name || "",
    productCode: product.product_code || product.productCode || product.code || "",
    status: product.status || "",
    quantity: getFirstNonNilValue(product.quantity, product.stock),
    costPrice: getServiceProductCostForForm(product),
    promotionPrice: getServiceProductPromotionPriceForForm(product),
    promotionStartedAt: getServiceProductPromotionStartedAt(product),
    promotionEndedAt: getServiceProductPromotionEndedAt(product),
    variantId: getFirstNonNilValue(selectedVariantId),
  };

  if (!isNil(isPromotion)) {
    normalizedProduct.isPromotion = isPromotion;
  }

  return normalizedProduct;
}

export function isEmptyDurations(durations) {
  return durations.some(
    (duration) =>
      isEmpty(duration.startAt) ||
      isEmpty(duration.endAt) ||
      isNil(duration.startAt) ||
      isNil(duration.endAt)
  );
}

export function normalizeServiceProducts(rawProducts) {
  const singleProductAsArray = rawProducts ? [rawProducts] : [];
  const products = Array.isArray(rawProducts)
    ? rawProducts
    : singleProductAsArray;

  return products.map((product) => normalizeServiceProduct(product));
}

export function getEmptyServiceProductFields() {
  return {
    serviceProductId: null,
    serviceProductName: "",
    serviceProductCode: "",
    serviceProductQuantity: null,
    serviceProductCost: null,
    serviceProducts: [],
  };
}

function clearAppointDurationSlotCosts(appointDurationSlots) {
  if (!Array.isArray(appointDurationSlots)) return {};

  return {
    appointDurationSlots: appointDurationSlots.map((slot) => ({
      ...slot,
      appointCost: null,
    })),
  };
}

export function getServiceProductFieldsForOptionToggle(
  isServiceProductType,
  appointDurationSlots
) {
  const costFields = {
    ...clearAppointDurationSlotCosts(appointDurationSlots),
    cost: null,
  };

  return isServiceProductType
    ? {
        ...getEmptyServiceProductFields(),
        ...costFields,
      }
    : costFields;
}

export function clearServiceProductsFromOptions(serviceOptions) {
  if (!serviceOptions) return serviceOptions;

  return serviceOptions.map((option) => ({
    ...option,
    variants: option.variants.map((variant) => {
      const { serviceProduct, ...variantWithoutServiceProduct } = variant;

      return variantWithoutServiceProduct;
    }),
  }));
}

export function getServiceProductOptionResetFields(serviceOptions) {
  if (!serviceOptions) return {};

  return {
    serviceOptions: clearServiceProductsFromOptions(serviceOptions),
    serviceOptionCosts: null,
    hasOptionSwitch: true,
  };
}

export function getServiceTypeChangeResetFields({
  currentServiceType,
  nextServiceType,
  serviceOptions,
}) {
  if (currentServiceType === nextServiceType) return {};

  const isDotLifeProductTransition =
    currentServiceType === "DOTLIFE_PRODUCT" ||
    nextServiceType === "DOTLIFE_PRODUCT";
  const serviceProductFields = isDotLifeProductTransition
    ? getEmptyServiceProductFields()
    : {};
  const serviceOptionFields = serviceOptions
    ? getServiceProductOptionResetFields(serviceOptions)
    : {};

  return {
    ...serviceOptionFields,
    ...serviceProductFields,
    cost: null,
  };
}

export function getUpdatedPrimaryServiceProducts(currentServiceProducts, product) {
  const optionServiceProducts = (currentServiceProducts || []).filter(
    (serviceProduct) => !isNil(serviceProduct.variantId)
  );

  if (!product) {
    return optionServiceProducts;
  }

  return [
    normalizeServiceProduct(product, null),
    ...optionServiceProducts,
  ];
}

export function formatServiceProductsForApi(serviceProducts, options = {}) {
  const selectedProducts = (serviceProducts || []).filter(
    (product) => !isNil(product.id)
  );
  const noOptionProducts = selectedProducts.filter(
    (product) => isNil(product.variantId)
  );
  const optionProducts = selectedProducts.filter(
    (product) => !isNil(product.variantId)
  );
  let productsForPayload = [];

  if (options.hasOptions === true) {
    productsForPayload = optionProducts;
  } else if (options.hasOptions === false) {
    productsForPayload = noOptionProducts.slice(0, 1);
  } else {
    productsForPayload = optionProducts.length > 0
      ? optionProducts
      : noOptionProducts.slice(0, 1);
  }

  return productsForPayload.map((product) => {
    const serviceProduct = {
      product_id: product.id,
    };

    if (!isNil(product.variantId)) {
      serviceProduct.variant_id = product.variantId;
    }

    return serviceProduct;
  });
}

export function getPrimaryServiceProduct(serviceProducts) {
  if (!serviceProducts || serviceProducts.length === 0) return null;

  const productWithoutVariant = serviceProducts.find(
    (product) => isNil(product.variantId)
  );

  return productWithoutVariant || serviceProducts[0];
}

export function getServiceProductSelectorValue({
  formValue = {},
  product = null,
} = {}) {
  const primaryProduct = product ||
    getPrimaryServiceProduct(formValue.serviceProducts);
  const serviceProductId = getFirstNonNilValue(
    formValue.serviceProductId,
    primaryProduct && primaryProduct.id
  );

  if (isNil(serviceProductId)) {
    return null;
  }

  let stock = formValue.serviceProductQuantity;
  let cost = formValue.serviceProductCost;

  if (isNil(stock) && primaryProduct) {
    stock = getFirstNonNilValue(
      primaryProduct.quantity,
      primaryProduct.stock
    );
  }

  if (primaryProduct && !isNil(primaryProduct.costPrice)) {
    cost = primaryProduct.costPrice;
  } else if (primaryProduct && !isNil(primaryProduct.cost)) {
    cost = primaryProduct.cost;
  } else if (primaryProduct && !isNil(primaryProduct.cost_price)) {
    cost = getServiceProductCostForForm(primaryProduct);
  }

  return {
    id: serviceProductId,
    name: formValue.serviceProductName ||
      (primaryProduct && primaryProduct.name) ||
      "",
    code: formValue.serviceProductCode ||
      (primaryProduct && primaryProduct.productCode) ||
      (primaryProduct && primaryProduct.code) ||
      (primaryProduct && primaryProduct.product_code) ||
      "",
    stock,
    cost,
    promotionPrice: primaryProduct
      ? getServiceProductPromotionPriceForForm(primaryProduct)
      : null,
    promotionStartedAt: getServiceProductPromotionStartedAt(primaryProduct),
    promotionEndedAt: getServiceProductPromotionEndedAt(primaryProduct),
    isPromotion: getServiceProductPromotionActiveFlag(primaryProduct),
  };
}

function getExistingCombinationCostId(
  serviceOptionCosts,
  variantIndex,
  combinationCostIndex
) {
  const optionCost = serviceOptionCosts && serviceOptionCosts[variantIndex];
  const combinationCost = optionCost &&
    optionCost.combination_costs &&
    optionCost.combination_costs[combinationCostIndex];

  return combinationCost ? combinationCost.id : null;
}

export function attachServiceProductsToVariants(
  serviceOptions,
  serviceProducts,
  serviceOptionCosts
) {
  if (!serviceOptions || !serviceProducts || serviceProducts.length === 0) {
    return serviceOptions;
  }

  const serviceProductByVariantId = serviceProducts.reduce((byVariantId, product) => {
    if (isNil(product.variantId)) {
      return byVariantId;
    }

    return { ...byVariantId, [product.variantId]: product };
  }, {});

  return serviceOptions.map((option) => ({
    ...option,
    variants: option.variants.map((variant, variantIndex) => {
      if (!isMainServiceOption(option)) {
        return variant;
      }

      const combinationCostId = getExistingCombinationCostId(
        serviceOptionCosts,
        variantIndex,
        0
      );
      const serviceProduct = serviceProductByVariantId[combinationCostId];

      return serviceProduct
        ? { ...variant, serviceProduct }
        : variant;
    }),
  }));
}

export function getServiceProductsFromOptions(serviceOptions, serviceOptionCosts) {
  if (!serviceOptions) return [];

  return serviceOptions.reduce((products, option) => {
    if (!isMainServiceOption(option)) {
      return products;
    }

    const optionProducts = option.variants
      .map((variant, variantIndex) => ({ variant, variantIndex }))
      .filter(
        ({ variant }) =>
          variant.serviceProduct && !isNil(variant.serviceProduct.id)
      )
      .map(({ variant, variantIndex }) =>
        normalizeServiceProduct(
          variant.serviceProduct,
          getExistingCombinationCostId(serviceOptionCosts, variantIndex, 0)
        )
      );

    return [...products, ...optionProducts];
  }, []);
}

export function formatFormServiceProductsForApi({
  serviceProducts,
  serviceOptions,
  serviceOptionCosts,
  hasServiceProduct = false,
  serviceType = null,
}) {
  const isServiceProductType = serviceType === "DOTLIFE_PRODUCT";

  if (!hasServiceProduct || !isServiceProductType) {
    return [];
  }

  const hasServiceOptions = Array.isArray(serviceOptions) &&
    serviceOptions.length > 0;
  const selectedProducts = hasServiceOptions
    ? getServiceProductsFromOptions(serviceOptions, serviceOptionCosts)
    : serviceProducts;

  return formatServiceProductsForApi(selectedProducts, {
    hasOptions: hasServiceOptions,
  });
}

export function getServiceProductsFieldForApi({
  includeEmpty = false,
  serviceProducts,
  serviceOptions,
  serviceOptionCosts,
  hasServiceProduct = false,
  serviceType = null,
}) {
  const serviceProductsPayload = formatFormServiceProductsForApi({
    serviceProducts,
    serviceOptions,
    serviceOptionCosts,
    hasServiceProduct,
    serviceType,
  });

  if (!includeEmpty && serviceProductsPayload.length === 0) return {};

  return {
    service_products: serviceProductsPayload,
  };
}

export function isEmptyServiceOptionCostValue(cost) {
  if (isNil(cost)) return true;

  if (typeof cost === "string" && cost.trim() === "") {
    return true;
  }

  return parseCurrencyInputNumber(cost) === null;
}

export function setupNewServiceOptions(serviceOptions) {
  return serviceOptions.map((option) => ({
    id: option.id,
    extra_option: option.extra_option,
    title: option.title,
    variants: option.variants.map((variant) => ({
      variant_id: variant.variant_id,
      is_not_sale: variant.is_not_sale,
      is_out_of_stock: variant.is_out_of_stock,
      title: variant.title,
      material_code: variant.material_code,
      material_quantity: variant.material_quantity,
    })),
  }));
}

export function setupNewServiceOptionCosts(serviceOptionCosts) {
  return serviceOptionCosts.map((optionCost) => ({
    id: optionCost.id,
    is_product: optionCost.is_product,
    title: optionCost.title,
    combination_costs: optionCost.combination_costs.map((combinationCost) => ({
      id: combinationCost.id,
      is_not_sale: combinationCost.is_not_sale,
      is_out_of_stock: combinationCost.is_out_of_stock,
      title: combinationCost.title,
      appointment_costs: combinationCost.appointment_costs.map((appointmentCost) => ({
        appoint_no: appointmentCost.appoint_no,
        cost: isEmptyServiceOptionCostValue(appointmentCost.cost)
          ? null
          : parseCurrencyInputNumber(appointmentCost.cost),
      })),
      material_code: combinationCost.material_code,
      material_quantity: combinationCost.material_quantity,
    })),
  }));
}

export function getServiceOptionsPayloadForApi({
  serviceOptions,
  serviceOptionCosts,
}) {
  return {
    service_options: serviceOptions
      ? setupNewServiceOptions(serviceOptions)
      : null,
    service_option_costs: serviceOptionCosts
      ? setupNewServiceOptionCosts(serviceOptionCosts)
      : null,
  };
}

export function getServiceMaterialFieldsForApi({
  materialCode,
  materialQuantity,
  serviceOptions,
}) {
  if (serviceOptions) {
    return {
      material_code: null,
      material_quantity: null,
    };
  }

  return {
    material_code: materialCode,
    material_quantity: materialQuantity ? +materialQuantity : null,
  };
}

export function hasMissingVariantServiceProduct(serviceOptions) {
  if (!serviceOptions) return false;

  return serviceOptions
    .filter(isMainServiceOption)
    .some((option) =>
      option.variants.some((variant) =>
        !variant.serviceProduct || isNil(variant.serviceProduct.id)
      )
    );
}

export function hasDuplicateVariantServiceProduct(serviceOptions) {
  if (!serviceOptions) return false;

  const selectedProductIds = serviceOptions.reduce((productIds, option) => {
    if (!isMainServiceOption(option)) {
      return productIds;
    }

    const optionProductIds = option.variants
      .filter(
        (variant) =>
          variant.serviceProduct && !isNil(variant.serviceProduct.id)
      )
      .map((variant) => String(variant.serviceProduct.id));

    return [...productIds, ...optionProductIds];
  }, []);
  const selectedProductIdMap = {};

  return selectedProductIds.some((productId) => {
    if (selectedProductIdMap[productId]) {
      return true;
    }

    selectedProductIdMap[productId] = true;

    return false;
  });
}

function isBlankText(value) {
  return !value || String(value).trim() === "";
}

export function getServiceOptionSummaryValidationMessage(
  serviceOptions,
  { isServiceProductType = false } = {}
) {
  if (!serviceOptions) return null;

  const mainOption = serviceOptions.find(isMainServiceOption);
  const extraOption = serviceOptions.find((option) => option.extra_option === true);

  if (mainOption && isBlankText(mainOption.title)) {
    return "กรุณากรอก Title (option หลัก) ให้ครบถ้วน";
  }

  if (extraOption && isBlankText(extraOption.title)) {
    return "กรุณากรอก Title (option เสริม) ให้ครบถ้วน";
  }

  const hasEmptyVariantTitle = serviceOptions.some((option) =>
    option.variants.some((variant) => isBlankText(variant.title))
  );

  if (hasEmptyVariantTitle) {
    return "กรุณากรอก Variant ให้ครบถ้วน";
  }

  if (
    isServiceProductType &&
    hasMissingVariantServiceProduct(serviceOptions)
  ) {
    return "กรุณาเลือก Product Name/Product Code ให้ครบถ้วน";
  }

  if (
    isServiceProductType &&
    hasDuplicateVariantServiceProduct(serviceOptions)
  ) {
    return "ไม่สามารถเลือกสินค้าซ้ำกันในแต่ละ Variant ได้";
  }

  return null;
}

export function getSelectedServiceProductIdsFromOptions(
  serviceOptions,
  ignoredVariantId = null
) {
  if (!serviceOptions) return [];

  return serviceOptions.reduce((productIds, option) => {
    if (!isMainServiceOption(option)) {
      return productIds;
    }

    const optionProductIds = option.variants
      .filter((variant) =>
        variant.variant_id !== ignoredVariantId &&
        variant.serviceProduct &&
        !isNil(variant.serviceProduct.id)
      )
      .map((variant) => variant.serviceProduct.id);

    return [...productIds, ...optionProductIds];
  }, []);
}

function getExistingOptionCostId(serviceOptionCosts, variantIndex) {
  const optionCost = serviceOptionCosts && serviceOptionCosts[variantIndex];

  return optionCost ? optionCost.id : null;
}

function getExistingCombinationAppointmentCost(
  serviceOptionCosts,
  variantIndex,
  combinationCostIndex,
  appointNo
) {
  const optionCost = serviceOptionCosts && serviceOptionCosts[variantIndex];
  const combinationCost = optionCost &&
    optionCost.combination_costs &&
    optionCost.combination_costs[combinationCostIndex];
  const appointmentCost = combinationCost &&
    combinationCost.appointment_costs &&
    combinationCost.appointment_costs.find(
      (item) => item.appoint_no === appointNo
    );

  return appointmentCost ? appointmentCost.cost : null;
}

export function normalizeServiceOptionCostsForForm({
  serviceOptionCosts,
  hasServiceProduct,
}) {
  if (!serviceOptionCosts || serviceOptionCosts.length === 0) return null;

  return serviceOptionCosts.map((serviceOptionCost) => {
    const combinationCosts = serviceOptionCost.combination_costs || [];

    return {
      ...serviceOptionCost,
      read_only_cost: hasServiceProduct,
      combination_costs: combinationCosts.map((combinationCost) => {
        const appointmentCosts = combinationCost.appointment_costs || [];

        return {
          ...combinationCost,
          appointment_costs: appointmentCosts.map((appointmentCost) => ({
            ...appointmentCost,
          })),
        };
      }),
    };
  });
}

function getCombinationCostReadOnly(
  serviceOptionCost,
  combinationCost,
  combinationCostIndex
) {
  if (!isNil(combinationCost.read_only_cost)) {
    return combinationCost.read_only_cost === true;
  }

  return serviceOptionCost.read_only_cost === true &&
    combinationCostIndex === 0;
}

function createAppointmentSlots(quota) {
  return quota > 0
    ? new Array(quota).fill(0)
    : new Array(1).fill(0);
}

function getServiceProductOptionVariants(mainVariant, serviceExtraOption) {
  const extraVariants = serviceExtraOption && serviceExtraOption.variants
    ? serviceExtraOption.variants
    : [];

  return [mainVariant, ...extraVariants];
}

function getServiceProductCostWithPromotion(serviceProduct) {
  const productCostData = getServiceProductCostData(serviceProduct);

  return {
    cost: productCostData.cost,
    promotionData: {
      hasPromotion: productCostData.hasPromotion,
      promotionPrice: productCostData.promotionPrice,
      originalCost: productCostData.originalCost,
      promotionStartedAt: productCostData.promotionStartedAt,
      promotionEndedAt: productCostData.promotionEndedAt,
    },
  };
}

function getServiceProductPromotionFields(promotionData) {
  if (!promotionData.hasPromotion) {
    return {};
  }

  return {
    promotion_price: promotionData.promotionPrice,
    promotion_original_cost: promotionData.originalCost,
    promotion_started_at: promotionData.promotionStartedAt,
    promotion_ended_at: promotionData.promotionEndedAt,
  };
}

function buildServiceProductAppointmentCosts({
  appointmentSlots,
  isMainCombination,
  cost,
  currentServiceOptionCosts,
  variantIndex,
  combinationCostIndex,
}) {
  return appointmentSlots.map((_, index) => {
    const appointNo = index + 1;

    return {
      appoint_no: appointNo,
      cost: isMainCombination
        ? cost
        : getExistingCombinationAppointmentCost(
          currentServiceOptionCosts,
          variantIndex,
          combinationCostIndex,
          appointNo
        ),
    };
  });
}

function buildServiceProductCombinationCost({
  optionVariant,
  combinationCostIndex,
  variantIndex,
  appointmentSlots,
  currentServiceOptionCosts,
  createId,
}) {
  const id =
    getExistingCombinationCostId(
      currentServiceOptionCosts,
      variantIndex,
      combinationCostIndex
    ) ||
    createId();
  const isMainCombination = combinationCostIndex === 0;
  const serviceProduct = isMainCombination
    ? optionVariant.serviceProduct
    : null;
  const { cost, promotionData } =
    getServiceProductCostWithPromotion(serviceProduct);

  return {
    id,
    is_not_sale: optionVariant.is_not_sale,
    is_out_of_stock: optionVariant.is_out_of_stock,
    material_code: optionVariant.material_code,
    title: optionVariant.title,
    read_only_cost: isMainCombination,
    ...getServiceProductPromotionFields(promotionData),
    appointment_costs: buildServiceProductAppointmentCosts({
      appointmentSlots,
      isMainCombination,
      cost,
      currentServiceOptionCosts,
      variantIndex,
      combinationCostIndex,
    }),
    material_quantity: optionVariant.material_quantity,
  };
}

export function setupServiceProductOptionCosts({
  serviceOptions,
  quota,
  currentServiceOptionCosts = null,
  createId,
}) {
  const serviceMainOption = serviceOptions
    ? serviceOptions.find(isMainServiceOption)
    : null;
  const serviceExtraOption = serviceOptions
    ? serviceOptions.find((option) => option.extra_option === true)
    : null;

  if (!serviceMainOption || !serviceMainOption.variants) return undefined;

  const appointmentSlots = createAppointmentSlots(quota);
  const isProduct = quota <= 0;

  return serviceMainOption.variants.map((variant, variantIndex) => {
    const optionId =
      getExistingOptionCostId(currentServiceOptionCosts, variantIndex) ||
      createId();
    const optionVariants =
      getServiceProductOptionVariants(variant, serviceExtraOption);
    const combinationCosts = optionVariants.map(
      (optionVariant, combinationCostIndex) =>
        buildServiceProductCombinationCost({
          optionVariant,
          combinationCostIndex,
          variantIndex,
          appointmentSlots,
          currentServiceOptionCosts,
          createId,
        })
    );

    return {
      id: optionId,
      title: variant.title,
      is_product: isProduct,
      read_only_cost: true,
      combination_costs: combinationCosts,
    };
  });
}

function hasCombinationPromotion(combinationCost, isExtraOption) {
  return !isExtraOption &&
    !isNil(combinationCost.promotion_price) &&
    !isNil(combinationCost.promotion_original_cost) &&
    !hasPromotionPeriodEnded(combinationCost.promotion_ended_at);
}

function hasCombinationPromotionPeriodMismatch(combinationCost, isExtraOption) {
  return !isExtraOption &&
    combinationCost.promotion_period_mismatch === true &&
    !hasPromotionPeriodEnded(combinationCost.promotion_ended_at);
}

function getCombinationPromotionPeriod(combinationCost) {
  return formatThaiShortBuddhistDateRange(
    combinationCost.promotion_started_at,
    combinationCost.promotion_ended_at
  );
}

function getServiceProductPromotionCombinationCost(serviceProduct) {
  const productCostData = getServiceProductCostData(serviceProduct);

  if (!productCostData.hasPromotion) return {};

  return {
    promotion_price: productCostData.promotionPrice,
    promotion_original_cost: productCostData.originalCost,
    promotion_started_at: productCostData.promotionStartedAt,
    promotion_ended_at: productCostData.promotionEndedAt,
  };
}

function getOptionProductByVariantId(serviceProducts, variantId) {
  return (serviceProducts || []).find((product) =>
    String(product.variantId || product.variant_id) === String(variantId)
  );
}

function getServiceOptionSummaryProduct({
  serviceOptions,
  serviceProducts,
  serviceOptionCostIndex,
  serviceOptionCost,
}) {
  const mainOption = serviceOptions
    ? serviceOptions.find(isMainServiceOption)
    : null;
  const variant = mainOption &&
    mainOption.variants &&
    mainOption.variants[serviceOptionCostIndex];

  if (!variant) return null;
  if (variant.serviceProduct) return variant.serviceProduct;

  const combinationCostId = serviceOptionCost &&
    serviceOptionCost.combination_costs &&
    serviceOptionCost.combination_costs[0] &&
    serviceOptionCost.combination_costs[0].id;

  return getOptionProductByVariantId(serviceProducts, combinationCostId) || null;
}

function hasCombinationPromotionMetadata(combinationCost) {
  return !isNil(combinationCost.promotion_price) ||
    !isNil(combinationCost.promotion_original_cost) ||
    combinationCost.promotion_period_mismatch === true;
}

function buildServiceOptionCostSummaryRow({
  serviceOptionCost,
  serviceOptionCostIndex,
  combinationCost,
  combinationCostIndex,
  appointmentCost,
  appointmentCostIndex,
  serviceProduct,
}) {
  const isExtraOption = combinationCostIndex > 0;
  const productPromotionCombinationCost =
    getServiceProductPromotionCombinationCost(
      isExtraOption ? null : serviceProduct
    );
  const combinationCostWithPromotion =
    hasCombinationPromotionMetadata(combinationCost)
      ? combinationCost
      : {
          ...combinationCost,
          ...productPromotionCombinationCost,
        };
  const hasPromotion =
    hasCombinationPromotion(combinationCostWithPromotion, isExtraOption);
  const hasPromotionPeriodMismatch =
    hasCombinationPromotionPeriodMismatch(
      combinationCostWithPromotion,
      isExtraOption
    );
  const promotionPeriod = hasPromotion
    ? getCombinationPromotionPeriod(combinationCostWithPromotion)
    : null;

  return {
    key: `${serviceOptionCostIndex}-${combinationCostIndex}-${appointmentCostIndex}`,
    serviceOptionCostIndex,
    combinationCostIndex,
    appointmentCostIndex,
    optionTitle: combinationCost.title,
    isExtraOption,
    appointNo: appointmentCost.appoint_no,
    cost: appointmentCost.cost,
    readOnly: getCombinationCostReadOnly(
      serviceOptionCost,
      combinationCost,
      combinationCostIndex
    ),
    hasPromotion,
    originalCost: hasPromotion
      ? combinationCostWithPromotion.promotion_original_cost
      : null,
    promotionPeriod,
    hasPromotionPeriodMismatch,
  };
}

function buildCombinationCostSummaryRows({
  serviceOptionCost,
  serviceOptionCostIndex,
  combinationCost,
  combinationCostIndex,
  serviceProduct,
}) {
  const appointmentCosts = combinationCost.appointment_costs || [];

  return appointmentCosts.map((appointmentCost, appointmentCostIndex) =>
    buildServiceOptionCostSummaryRow({
      serviceOptionCost,
      serviceOptionCostIndex,
      combinationCost,
      combinationCostIndex,
      appointmentCost,
      appointmentCostIndex,
      serviceProduct,
    })
  );
}

function buildServiceOptionCostSummaryRows(
  serviceOptionCost,
  serviceOptionCostIndex,
  options = {}
) {
  const combinationCosts = serviceOptionCost.combination_costs || [];
  const serviceProduct = getServiceOptionSummaryProduct({
    serviceOptions: options.serviceOptions,
    serviceProducts: options.serviceProducts,
    serviceOptionCostIndex,
    serviceOptionCost,
  });

  return combinationCosts.reduce(
    (combinationRows, combinationCost, combinationCostIndex) => {
      const appointmentRows = buildCombinationCostSummaryRows({
        serviceOptionCost,
        serviceOptionCostIndex,
        combinationCost,
        combinationCostIndex,
        serviceProduct,
      });

      combinationRows.push(...appointmentRows);

      return combinationRows;
    },
    []
  );
}

export function getServiceOptionCostSummaryRows(
  serviceOptionCosts,
  options = {}
) {
  if (!serviceOptionCosts) return [];

  return serviceOptionCosts.reduce((rows, serviceOptionCost, index) => {
    const optionRows =
      buildServiceOptionCostSummaryRows(serviceOptionCost, index, options);

    rows.push(...optionRows);

    return rows;
  }, []);
}

export function getServiceOptionCostSummaryPromotion(summaryRows) {
  const promotionRows = (summaryRows || [])
    .filter((row) => row.hasPromotion);
  const hasPromotionPeriodMismatch = (summaryRows || [])
    .some((row) => row.hasPromotionPeriodMismatch);
  const promotionPeriods = promotionRows
    .map((row) => row.promotionPeriod)
    .filter((period) => !isNil(period));
  const uniquePromotionPeriods = [...new Set(promotionPeriods)];

  if (promotionRows.length === 0) {
    return {
      hasPromotion: false,
      promotionPeriod: null,
      hasPromotionPeriodMismatch,
    };
  }

  if (hasPromotionPeriodMismatch) {
    return {
      hasPromotion: false,
      promotionPeriod: null,
      hasPromotionPeriodMismatch: true,
    };
  }

  if (uniquePromotionPeriods.length === 0) {
    return {
      hasPromotion: true,
      promotionPeriod: null,
      hasPromotionPeriodMismatch,
    };
  }

  if (uniquePromotionPeriods.length === 1 && !hasPromotionPeriodMismatch) {
    return {
      hasPromotion: true,
      promotionPeriod: uniquePromotionPeriods[0],
      hasPromotionPeriodMismatch: false,
    };
  }

  return {
    hasPromotion: false,
    promotionPeriod: null,
    hasPromotionPeriodMismatch: true,
  };
}

export function shouldUseOptionCostSummary(serviceOptionCosts) {
  return Array.isArray(serviceOptionCosts) &&
    serviceOptionCosts.length > 0;
}

function isMatchingServiceOptionCostType(combinationCostIndex, options = {}) {
  if (isNil(options.isExtraOption)) return true;

  return options.isExtraOption
    ? combinationCostIndex > 0
    : combinationCostIndex === 0;
}

export function hasEmptyServiceOptionCosts(serviceOptionCosts, options = {}) {
  if (!serviceOptionCosts) return false;

  return serviceOptionCosts.some((appointOption) =>
    appointOption.combination_costs.some(
      (combineOption, combinationCostIndex) =>
        isMatchingServiceOptionCostType(combinationCostIndex, options) &&
        combineOption.appointment_costs.some((item) =>
          isEmptyServiceOptionCostValue(item.cost)
        )
    )
  );
}

export function formatDurationsForApi(durations) {
  return durations.map(({ startAt, endAt }) => ({
    start_at: DateTime.fromString(startAt, "HH:mm").toFormat("HH:mm:ssZZ"),
    end_at: DateTime.fromString(endAt, "HH:mm").toFormat("HH:mm:ssZZ"),
  }));
}

function getServiceFormValidations({
  form,
  hasEmptyDuration,
  hasEmptyAppointmentCost = () => false,
  hasEmptyCombineOption = () => false,
  hasEmptyOptionCost = () => false,
  hasEmptyExtraOptionCost = () => false,
  isCheckCombineOption,
  shouldValidateOptionCosts = () => false,
  validateIcon = false,
  validateCreateCost = false,
  validateMaterialCode = false,
  validateSapRecognizeRevenue = false,
  minimumAdvanceDurationMessage,
}) {
  const validations = [
    {
      check: () => !form.name || !form.name.trim(),
      message: "กรุณากรอกชื่อบริการ",
    },
    {
      check: () => form.minimumAdvanceDuration <= 0,
      message: minimumAdvanceDurationMessage,
    },
    {
      check: hasEmptyDuration,
      message: "กรุณาใส่ช่วงเวลาให้ครบ",
    },
  ];

  if (validateIcon) {
    validations.push({ check: () => !form.iconFile, message: "กรุณาใส่รูป" });
  }

  if (validateCreateCost) {
    validations.push({
      check: () =>
        !form.hasServiceProduct &&
        hasEmptyAppointmentCost() &&
        !form.serviceOptions,
      message: "กรุณากรอกราคาต้นทุนให้ครบ",
    });
  }

  validations.push({ check: () => !form.provider, message: "กรุณาเลือกผู้บริการ" });

  if (validateCreateCost) {
    validations.push({
      check: () =>
        !form.cost &&
        form.cost !== 0 &&
        form.quota === 0 &&
        !form.serviceOptions &&
        !form.hasServiceProduct,
      message: "กรุณากรอกต้นทุนรวม",
    });
  }

  validations.push(
    {
      check: () => isCheckCombineOption,
      message: "มีการเปลี่ยน option กรุณากดสรุปราคาทุน",
    },
    {
      check: () => shouldValidateOptionCosts() && hasEmptyOptionCost(),
      message: "กรุณากรอกราคาต้นทุน option หลัก",
    },
    {
      check: () => shouldValidateOptionCosts() && hasEmptyExtraOptionCost(),
      message: "กรุณากรอกราคาต้นทุน option เสริม",
    },
    {
      check: () =>
        shouldValidateOptionCosts() &&
        !hasEmptyOptionCost() &&
        !hasEmptyExtraOptionCost() &&
        hasEmptyCombineOption(),
      message: "กรุณากรอกราคาต้นทุน option",
    }
  );

  if (validateMaterialCode) {
    validations.push(
      {
        check: () =>
          !form.serviceOptionCosts &&
          !form.serviceOptions &&
          !form.materialCode &&
          (form.sapServiceType === "NORMAL" || form.hasServiceProduct),
        message: "กรุณากรอก Material Code",
      },
      {
        check: () =>
          !form.serviceOptionCosts &&
          !form.serviceOptions &&
          form.materialCode &&
          !form.materialQuantity &&
          form.sapServiceType === "NORMAL",
        message: "กรุณากรอก จำนวน / หน่วย",
      }
    );
  }

  validations.push(
    {
      check: () =>
        form.hasServiceProduct &&
        !form.serviceOptions &&
        !form.serviceProductId,
      message: "กรุณาเลือกสินค้าจากรายการ",
    },
    {
      check: () =>
        form.hasServiceProduct &&
        !form.serviceOptions &&
        (!form.serviceProductCost || form.serviceProductCost <= 0),
      message: "ราคาต้นทุนต้องมีค่ามากกว่า 0",
    },
    {
      check: () =>
        form.hasServiceProduct &&
        form.serviceOptions &&
        hasDuplicateVariantServiceProduct(form.serviceOptions),
      message: "ไม่สามารถเลือกสินค้าซ้ำกันในแต่ละ Variant ได้",
    },
    {
      check: () => form.serviceOptions && form.hasOptionSwitch,
      message: "กรุณากดสรุปราคาทุน",
    }
  );

  if (validateSapRecognizeRevenue) {
    validations.push({
      check: () =>
        form.quota > 0 &&
        form.sapServiceType === "NORMAL" &&
        !form.sapServiceRecognizeRevenue,
      message: "กรุณาเลือก รับรู้รายได้บน SAP",
    });
  }

  return validations;
}

export function getCreateServiceFormValidations({
  form,
  hasEmptyDuration,
  hasEmptyAppointmentCost,
  hasEmptyCombineOption,
  hasEmptyOptionCost,
  hasEmptyExtraOptionCost,
  isCheckCombineOption,
  shouldValidateOptionCosts,
  minimumAdvanceDurationMessage,
}) {
  return getServiceFormValidations({
    form,
    hasEmptyDuration,
    hasEmptyAppointmentCost,
    hasEmptyCombineOption,
    hasEmptyOptionCost,
    hasEmptyExtraOptionCost,
    isCheckCombineOption,
    shouldValidateOptionCosts,
    validateIcon: true,
    validateCreateCost: true,
    validateMaterialCode: true,
    validateSapRecognizeRevenue: true,
    minimumAdvanceDurationMessage,
  });
}

export function getDetailServiceFormValidations({
  form,
  hasEmptyDuration,
  hasEmptyCombineOption,
  hasEmptyOptionCost,
  hasEmptyExtraOptionCost,
  isCheckCombineOption,
  shouldValidateOptionCosts,
  minimumAdvanceDurationMessage,
}) {
  return getServiceFormValidations({
    form,
    hasEmptyDuration,
    hasEmptyCombineOption,
    hasEmptyOptionCost,
    hasEmptyExtraOptionCost,
    isCheckCombineOption,
    shouldValidateOptionCosts,
    minimumAdvanceDurationMessage,
  });
}
